import { useState, useEffect } from 'react'
import { useSupabaseAuth } from '../lib/useSupabaseAuth.jsx'

// Authentication Error Boundary Component
export const AuthErrorBoundary = ({ children, fallback = null }) => {
  const { authError, loading, authReady, isAuthenticated } = useSupabaseAuth()
  const [hasError, setHasError] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Handle authentication errors
    if (authError && !loading && authReady) {
      // Transient navigator-lock contention (Supabase auth lock) is not a real
      // auth failure — ignore so we don't bounce a logged-in user to login.
      const isTransient =
        authError?.name === 'AbortError' ||
        authError?.message === 'Session timeout' ||
        authError?.message?.includes('signal is aborted')

      if (isTransient) {
        console.warn('[AuthErrorBoundary] Ignoring transient auth error:', authError?.message)
        return
      }

      console.error('[AuthErrorBoundary] Auth error detected:', authError)
      setError(authError)
      setHasError(true)

      // If it's an authentication timeout or RLS error, redirect to login
      if (authError.message?.includes('timed out') ||
          authError.message?.includes('RLS') ||
          authError.message?.includes('permission') ||
          authError.message?.includes('PGRST116')) {
        console.log('[AuthErrorBoundary] Redirecting to login due to auth error')
        // Small delay to allow error display
        setTimeout(() => {
          window.location.href = '/'
        }, 2000)
      }
    }
  }, [authError, loading, authReady])

  // Show fallback if provided
  if (fallback && hasError) {
    return fallback
  }

  // Show error UI if no fallback
  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-mint-50 to-primary-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-20 h-20 rounded-2xl bg-orange-500 flex items-center justify-center shadow-glow mb-6 mx-auto">
            <i className="fas fa-exclamation-triangle text-white text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-primary mb-4">Authentication Error</h1>
          <p className="text-secondary mb-4">
            {error?.message || 'An authentication error occurred'}
          </p>
          <p className="text-sm text-muted mb-6">
            Redirecting to login...
          </p>
          <button 
            onClick={() => window.location.href = '/'}
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Go to Login Now
          </button>
        </div>
      </div>
    )
  }

  // Show children if no errors
  return children
}

// Enhanced error boundary with retry logic
export const EnhancedErrorBoundary = ({ children }) => {
  const [hasError, setHasError] = useState(false)
  const [error, setError] = useState(null)
  const [retryCount, setRetryCount] = useState(0)
  const maxRetries = 3

  const handleError = (error, errorInfo) => {
    console.error('[EnhancedErrorBoundary] Error caught:', error, errorInfo)
    setError(error)
    setHasError(true)
  }

  const handleRetry = () => {
    if (retryCount < maxRetries) {
      console.log(`[EnhancedErrorBoundary] Retrying... (${retryCount + 1}/${maxRetries})`)
      setRetryCount(prev => prev + 1)
      setHasError(false)
      setError(null)
    } else {
      console.log('[EnhancedErrorBoundary] Max retries reached, redirecting to login')
      window.location.href = '/'
    }
  }

  const handleReset = () => {
    console.log('[EnhancedErrorBoundary] Resetting error state')
    setHasError(false)
    setError(null)
    setRetryCount(0)
  }

  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-mint-50 to-primary-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-20 h-20 rounded-2xl bg-red-500 flex items-center justify-center shadow-glow mb-6 mx-auto">
            <i className="fas fa-exclamation-triangle text-white text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-primary mb-4">Something went wrong</h1>
          <p className="text-secondary mb-4">
            {error?.message || 'An unexpected error occurred'}
          </p>
          <p className="text-sm text-muted mb-6">
            Retry attempts: {retryCount}/{maxRetries}
          </p>
          <div className="space-y-3">
            {retryCount < maxRetries && (
              <button 
                onClick={handleRetry}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Try Again
              </button>
            )}
            <button 
              onClick={handleReset}
              className="w-full bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Reset
            </button>
            <button 
              onClick={() => window.location.href = '/'}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  return children
} 